import { DashboardDomain } from '../../types'
import {
    DailyMeasure,
    DailyRow,
    dailyLevelChanges,
    dailyLevels,
    dailyRates,
    dailySeries,
    levelOn,
    levelPairOn,
    sumMeasure,
    zeroWhereNoDayHasAnythingToDivideBy,
} from '../../math/series'
import { RateTrend, contextFor, levelTrend, outlierDays, rateTrend, trend } from '../../math/statistics'
import {
    DateWindow,
    IsoDate,
    baselineWindowFor,
    comparisonWindowFor,
    datesIn,
    lengthInDays,
    shiftIsoDate,
    weekdayIndexOf,
} from '../../math/windows'
import { NO_VALUE, formatCountDelta, formatCountInFull, formatMoneyInFull, formatRate } from '../../math/format'
import { DashboardTemplate, TEMPLATE_SLOTS } from '../../template'
import { assertUnreachable } from '../../assert-unreachable'

export const MIXED_CURRENCIES = 'Mixed currencies'
export const NO_SENDS_IN_RANGE = 'No sends in range'

export type DomainCell = {
    /** The raw figure: a range total, a ratio, or a level. Null where the table shows a dash. */
    value: number | null
    /** The same figure over the comparison window; kept even where the table shows a dash. */
    prior: number | null
    formatted: string
    /** One entry per day in the range (per week for ranges over 90 days); a null day had no data. Empty when there is nothing to draw. */
    sparkline: Array<number | null>
    /** A fraction for `count` cells (0.142 is +14.2%) and percentage points for `rate` cells. */
    delta: { value: number | null; kind: 'count' | 'rate'; steady: boolean }
}

export type DomainRow = {
    domain: DashboardDomain
    pinned: boolean
    sentInRange: boolean
    cells: Record<string, DomainCell>
    context: {
        /** The range's rate against the domain's own 28-day average, as a fraction of that average. */
        pct: number | null
        /** At least one standard deviation from the baseline's own spread. */
        significant: boolean
        formatted: string
        label: string
        baselineMean: number | null
    }
    outlier?: { days: IsoDate[]; operational: boolean }
}

export type CellKey =
    | 'subscribers'
    | 'new_subscribers'
    | 'sends'
    | 'clicks'
    | 'impressions'
    | 'purchases'
    | 'revenue'
    | 'ctr'
    | 'purchase_rate'
    | 'revenue_per_send'

/**
 * How a column's figure is computed: a flow adds up across the range, a ratio is recomputed from summed parts, and a
 * level is read on the range's last day.
 */
type FlowSpec = { form: 'flow'; measure: DailyMeasure; money?: boolean }
type RatioSpec = {
    form: 'ratio'
    numerator: DailyMeasure
    denominator: DailyMeasure
    kind: 'rate' | 'money'
    purchaseRate?: boolean
}
type LevelSpec = { form: 'level' }
type CellSpec = FlowSpec | RatioSpec | LevelSpec

const FLOW_SPECS: Record<'new_subscribers' | 'sends' | 'clicks' | 'impressions' | 'purchases' | 'revenue', FlowSpec> = {
    new_subscribers: { form: 'flow', measure: 'subscriptions' },
    sends: { form: 'flow', measure: 'sends' },
    clicks: { form: 'flow', measure: 'clicks' },
    impressions: { form: 'flow', measure: 'impressions' },
    purchases: { form: 'flow', measure: 'purchases' },
    revenue: { form: 'flow', measure: 'purchaseAmount', money: true },
}

const RATIO_SPECS: Record<'ctr' | 'purchase_rate' | 'revenue_per_send', RatioSpec> = {
    ctr: { form: 'ratio', numerator: 'clicks', denominator: 'impressions', kind: 'rate' },
    purchase_rate: { form: 'ratio', numerator: 'purchases', denominator: 'clicks', kind: 'rate', purchaseRate: true },
    revenue_per_send: { form: 'ratio', numerator: 'purchaseAmount', denominator: 'sends', kind: 'money' },
}

const CELL_SPECS: Record<CellKey, CellSpec> = { subscribers: { form: 'level' }, ...FLOW_SPECS, ...RATIO_SPECS }

/**
 * Every figure a template's table, totals and CSV need. The table shows the template's own columns; the rest
 * (impressions for CTR, purchase rate) feed the totals row and the export.
 */
const CELL_KEYS_BY_TEMPLATE: Record<DashboardTemplate, CellKey[]> = {
    publisher: ['subscribers', 'new_subscribers', 'sends', 'clicks', 'impressions', 'ctr'],
    ecommerce: ['revenue', 'purchases', 'clicks', 'sends', 'new_subscribers', 'revenue_per_send', 'purchase_rate'],
}

/** The registry has no measure of its own for subscribers or revenue per send; each follows the measure it moves with. */
const GOOD_DIRECTION_MEASURE_BY_CELL_KEY: Record<string, string> = {
    subscribers: 'subscriptions',
    new_subscribers: 'subscriptions',
    sends: 'notifications_sent',
    clicks: 'clicks',
    impressions: 'impressions',
    ctr: 'ctr_decimal',
    purchases: 'purchases',
    revenue: 'purchase_amount',
    revenue_per_send: 'purchase_amount',
    purchase_rate: 'purchase_rate_decimal',
}

export const goodDirectionMeasureFor = (cellKey: string): string =>
    GOOD_DIRECTION_MEASURE_BY_CELL_KEY[cellKey] ?? cellKey

/** The context rate's own registry measure, so its colouring follows the registry like every other figure. */
export const contextGoodDirectionMeasureFor = (template: DashboardTemplate): string =>
    goodDirectionMeasureFor(TEMPLATE_SLOTS[template].contextRate)

export const CONTEXT_RATE_NAME_BY_TEMPLATE: Record<DashboardTemplate, string> = {
    publisher: 'CTR',
    ecommerce: 'purchase rate',
}

export const SINGLE_CURRENCY_CELL_KEYS = ['revenue', 'revenue_per_send']

const SPARKLINE_LEVEL_DAYS = 14
const MAXIMUM_DAILY_SPARKLINE_POINTS = 90
const DAYS_PER_WEEKLY_SPARKLINE_POINT = 7
/**
 * Send rhythms are weekly: a domain that mails on weekdays only, or only on Thursdays, is silent on its other days
 * by design. The 28-day baseline holds four of each weekday, and requiring three of the four lets a single holiday
 * or missed week pass without hiding a real stop.
 */
const MINIMUM_SENDING_DAYS_ON_SAME_BASELINE_WEEKDAY_FOR_OPERATIONAL_OUTLIER = 3

const withoutGaps = (values: Array<number | null>): number[] =>
    values.filter((value): value is number => value !== null)

const meanOf = (values: number[]): number | null =>
    values.length ? values.reduce((total, value) => total + value, 0) / values.length : null

/** Ranges over 90 days are drawn with one point per week, summed from the range's first day, so a chart stays readable. */
const bucketedWeekly = (daily: number[], range: DateWindow): number[] => {
    if (lengthInDays(range) <= MAXIMUM_DAILY_SPARKLINE_POINTS) return daily
    const weekly: number[] = []
    for (let start = 0; start < daily.length; start += DAYS_PER_WEEKLY_SPARKLINE_POINT) {
        weekly.push(
            daily.slice(start, start + DAYS_PER_WEEKLY_SPARKLINE_POINT).reduce((total, value) => total + value, 0),
        )
    }
    return weekly
}

const noDelta = (kind: 'count' | 'rate' = 'count') => ({ value: null, kind, steady: false })

const formatFlow = (value: number, money: boolean | undefined, currencyCode: string | undefined) =>
    money ? formatMoneyInFull(value, currencyCode) : formatCountInFull(value)

type DomainContext = {
    domain: DashboardDomain
    domainRows: DailyRow[]
    range: DateWindow
    comparisonWindow: DateWindow
    baselineWindow: DateWindow
    sentInRange: boolean
}

const flowCell = (context: DomainContext, { measure, money }: FlowSpec): DomainCell => {
    const { domain, domainRows, range, comparisonWindow, baselineWindow, sentInRange } = context
    const current = dailySeries(domainRows, measure, range)
    const flowTrend = trend(
        current,
        dailySeries(domainRows, measure, comparisonWindow),
        dailySeries(domainRows, measure, baselineWindow),
    )
    return {
        value: flowTrend.current,
        prior: flowTrend.prior,
        formatted: formatFlow(flowTrend.current, money, domain.currencyCode),
        sparkline: sentInRange ? bucketedWeekly(current, range) : [],
        delta:
            sentInRange && flowTrend.deltaPct !== null
                ? { value: flowTrend.deltaPct, kind: 'count', steady: flowTrend.steady }
                : noDelta(),
    }
}

/**
 * A money ratio is compared as a relative change; percentage points only make sense for a share. Shared by a
 * domain's row and the totals row so both read a change the same way. Nothing is compared when the range had no
 * sends, or when either period has nothing to divide by.
 */
const ratioDelta = (spec: RatioSpec, ratioTrend: RateTrend, rangeHadSends: boolean): DomainCell['delta'] => {
    const { current, prior } = ratioTrend
    if (!rangeHadSends || current === null || prior === null) return noDelta(spec.kind === 'money' ? 'count' : 'rate')
    return spec.kind === 'money'
        ? {
              value: prior === 0 ? null : (current - prior) / prior,
              kind: 'count',
              steady: ratioTrend.steady,
          }
        : { value: ratioTrend.deltaPts, kind: 'rate', steady: ratioTrend.steady }
}

const ratioCell = (context: DomainContext, spec: RatioSpec): DomainCell => {
    const { domain, domainRows, range, comparisonWindow, baselineWindow, sentInRange } = context
    const { numerator, denominator } = spec
    const ratioTrend = rateTrend(
        sumMeasure(domainRows, numerator, range),
        sumMeasure(domainRows, denominator, range),
        sumMeasure(domainRows, numerator, comparisonWindow),
        sumMeasure(domainRows, denominator, comparisonWindow),
        dailyRates(domainRows, numerator, denominator, baselineWindow),
    )
    const value = sentInRange ? ratioTrend.current : null
    const formatted =
        value === null
            ? NO_VALUE
            : spec.kind === 'money'
              ? formatMoneyInFull(value, domain.currencyCode)
              : formatRate(value, { purchaseRate: spec.purchaseRate })
    const numeratorByDay = bucketedWeekly(dailySeries(domainRows, numerator, range), range)
    const denominatorByDay = bucketedWeekly(dailySeries(domainRows, denominator, range), range)
    const delta = ratioDelta(spec, ratioTrend, sentInRange)
    return {
        value,
        prior: ratioTrend.prior,
        formatted,
        // A domain that sent has a chart that spans the range, a day with nothing to divide by drawn at zero; one that did not send has none.
        sparkline: sentInRange
            ? zeroWhereNoDayHasAnythingToDivideBy(
                  numeratorByDay.map((amount, index) =>
                      denominatorByDay[index] === 0 ? null : amount / denominatorByDay[index],
                  ),
              )
            : [],
        delta,
    }
}

const subscribersCell = (context: DomainContext): DomainCell => {
    const { domainRows, range, comparisonWindow, baselineWindow, sentInRange } = context
    const level = levelOn(domainRows, range.through)
    const priorLevel = levelOn(domainRows, comparisonWindow.through)
    if (level === null) return { value: null, prior: priorLevel, formatted: NO_VALUE, sparkline: [], delta: noDelta() }
    const levelPair = levelPairOn(domainRows, comparisonWindow.through, range.through)
    const levelChange = levelTrend(
        levelPair?.current ?? null,
        levelPair?.prior ?? null,
        dailyLevelChanges(domainRows, baselineWindow),
        lengthInDays(range),
    )
    return {
        value: level,
        prior: priorLevel,
        formatted: formatCountInFull(level),
        sparkline: dailyLevels(domainRows, {
            since: shiftIsoDate(range.through, -(SPARKLINE_LEVEL_DAYS - 1)),
            through: range.through,
        }),
        delta:
            sentInRange && levelChange.deltaPct !== null
                ? { value: levelChange.deltaPct, kind: 'count', steady: levelChange.steady }
                : noDelta(),
    }
}

const contextRatioSpec = (template: DashboardTemplate): RatioSpec => RATIO_SPECS[TEMPLATE_SLOTS[template].contextRate]

const contextFor28Days = (template: DashboardTemplate, context: DomainContext): DomainRow['context'] => {
    const { domainRows, range, baselineWindow, sentInRange } = context
    const { numerator, denominator } = contextRatioSpec(template)
    const baselineRates = dailyRates(domainRows, numerator, denominator, baselineWindow)
    const baselineMean = meanOf(withoutGaps(baselineRates))
    if (!sentInRange) {
        return { pct: null, significant: false, formatted: NO_VALUE, label: NO_SENDS_IN_RANGE, baselineMean }
    }
    const rangeDenominator = sumMeasure(domainRows, denominator, range)
    const rangeRate = rangeDenominator === 0 ? null : sumMeasure(domainRows, numerator, range) / rangeDenominator
    const { pct, significant } = contextFor(rangeRate, baselineRates)
    const rateName = CONTEXT_RATE_NAME_BY_TEMPLATE[template]
    return {
        pct,
        significant,
        formatted: pct === null ? NO_VALUE : formatCountDelta(pct),
        label:
            pct === null
                ? 'No 28-day baseline'
                : significant
                  ? `vs own 28-day ${rateName}`
                  : `steady vs own 28-day ${rateName}`,
        baselineMean,
    }
}

/**
 * Unusual days against the domain's own trailing 28 days. A day without sends is an operational outlier when the
 * domain normally sends on that weekday, which is a stop rather than an unusual rate, so it takes priority. Days
 * the domain never sends on are not outliers.
 */
const outlierFor = (template: DashboardTemplate, context: DomainContext): DomainRow['outlier'] => {
    const { domainRows, range, baselineWindow } = context
    const rangeDates = datesIn(range)
    const baselineSendsByDay = dailySeries(domainRows, 'sends', baselineWindow)
    const baselineSendingDaysByWeekday = new Map<number, number>()
    datesIn(baselineWindow).forEach((date, index) => {
        if (baselineSendsByDay[index] <= 0) return
        const weekday = weekdayIndexOf(date)
        baselineSendingDaysByWeekday.set(weekday, (baselineSendingDaysByWeekday.get(weekday) ?? 0) + 1)
    })
    const sendsByDay = dailySeries(domainRows, 'sends', range)
    const silentDaysOnSendingWeekdays = rangeDates.filter(
        (date, index) =>
            sendsByDay[index] === 0 &&
            (baselineSendingDaysByWeekday.get(weekdayIndexOf(date)) ?? 0) >=
                MINIMUM_SENDING_DAYS_ON_SAME_BASELINE_WEEKDAY_FOR_OPERATIONAL_OUTLIER,
    )
    if (silentDaysOnSendingWeekdays.length) return { days: silentDaysOnSendingWeekdays, operational: true }
    const { numerator, denominator } = contextRatioSpec(template)
    const rateByDay = dailyRates(domainRows, numerator, denominator, range)
    const valuesByDate: Record<IsoDate, number> = {}
    rangeDates.forEach((date, index) => {
        const rate = rateByDay[index]
        if (rate !== null) valuesByDate[date] = rate
    })
    const days = outlierDays(valuesByDate, withoutGaps(dailyRates(domainRows, numerator, denominator, baselineWindow)))
    return days.length ? { days, operational: false } : undefined
}

const cellFor = (spec: CellSpec, context: DomainContext): DomainCell => {
    switch (spec.form) {
        case 'flow':
            return flowCell(context, spec)
        case 'ratio':
            return ratioCell(context, spec)
        case 'level':
            return subscribersCell(context)
        default:
            return assertUnreachable(spec)
    }
}

const domainsIn = (domains: DashboardDomain[], selectedDomainIds: number[]): DashboardDomain[] =>
    selectedDomainIds.length ? domains.filter((domain) => selectedDomainIds.includes(domain.id)) : domains

/**
 * One row per domain in the filter. Every domain's figures come from its own daily rows: flows are summed over
 * the range, ratios are recomputed from summed parts, and subscribers is the level on the range's last day.
 */
export const buildDomainRows = (args: {
    template: DashboardTemplate
    rows: DailyRow[]
    domains: DashboardDomain[]
    /** Empty means every domain. */
    selectedDomainIds: number[]
    range: DateWindow
    /** Last Month is compared with the calendar month before it; see `comparisonWindowFor`. */
    rangeKey?: string
    pinnedIds: number[]
}): DomainRow[] => {
    const { template, rows, domains, selectedDomainIds, range, rangeKey, pinnedIds } = args
    const comparisonWindow = comparisonWindowFor(range, rangeKey)
    const baselineWindow = baselineWindowFor(range)
    const rowsByDomainId = new Map<number, DailyRow[]>()
    for (const row of rows) {
        const domainRows = rowsByDomainId.get(row.domainId)
        if (domainRows) domainRows.push(row)
        else rowsByDomainId.set(row.domainId, [row])
    }

    return domainsIn(domains, selectedDomainIds).map((domain) => {
        const domainRows = rowsByDomainId.get(domain.id) ?? []
        const context: DomainContext = {
            domain,
            domainRows,
            range,
            comparisonWindow,
            baselineWindow,
            sentInRange: sumMeasure(domainRows, 'sends', range) > 0,
        }
        const cells: Record<string, DomainCell> = {}
        for (const key of CELL_KEYS_BY_TEMPLATE[template]) {
            cells[key] = cellFor(CELL_SPECS[key], context)
        }
        return {
            domain,
            pinned: pinnedIds.includes(domain.id),
            sentInRange: context.sentInRange,
            cells,
            context: contextFor28Days(template, context),
            outlier: outlierFor(template, context),
        }
    })
}

const sumOfValues = (domainRows: DomainRow[], key: string): number =>
    domainRows.reduce((total, row) => total + (row.cells[key]?.value ?? 0), 0)

/** One footer cell: the figure as displayed. A total carries no change line, so the Total row is as tall as a row of figures. */
export type DomainTotalsCell = { text: string }

/** Keyed like the row cells, plus `context`, which has no total. */
export type DomainTotalsRow = Record<string, DomainTotalsCell>

/**
 * The totals row's figures as displayed. Flows add up; ratios are recomputed from the summed numerators and
 * denominators, never averaged. Context has no total. With mixed currencies no revenue figure is a total.
 */
export const buildTotalsRow = (args: {
    domainRows: DomainRow[]
    template: DashboardTemplate
    mixedCurrency: boolean
    /** The daily rows the domain rows were built from; the totals are taken over the domains that appear in `domainRows`. */
    rows: DailyRow[]
    range: DateWindow
}): DomainTotalsRow => {
    const { domainRows, template, mixedCurrency, rows, range } = args
    // Every domain shares one currency whenever a revenue total is shown; with mixed currencies none is.
    const currencyCode = domainRows[0]?.domain.currencyCode
    const domainIds = domainRows.map((row) => row.domain.id)
    const totalsCellFor = (key: CellKey): DomainTotalsCell => {
        const spec = CELL_SPECS[key]
        if (SINGLE_CURRENCY_CELL_KEYS.includes(key) && mixedCurrency) return { text: MIXED_CURRENCIES }
        switch (spec.form) {
            case 'flow':
                return { text: formatFlow(sumOfValues(domainRows, key), spec.money, currencyCode) }
            case 'ratio': {
                const { numerator, denominator } = spec
                const denominatorTotal = sumMeasure(rows, denominator, range, domainIds)
                if (denominatorTotal === 0) return { text: NO_VALUE }
                const ratio = sumMeasure(rows, numerator, range, domainIds) / denominatorTotal
                return {
                    text:
                        spec.kind === 'money'
                            ? formatMoneyInFull(ratio, currencyCode)
                            : formatRate(ratio, { purchaseRate: spec.purchaseRate }),
                }
            }
            case 'level': {
                const levels = domainRows.map((row) => row.cells[key]?.value).filter((v): v is number => v != null)
                return {
                    text: levels.length
                        ? formatCountInFull(levels.reduce((total, level) => total + level, 0))
                        : NO_VALUE,
                }
            }
            default:
                return assertUnreachable(spec)
        }
    }
    const totals: DomainTotalsRow = { context: { text: '' } }
    for (const key of CELL_KEYS_BY_TEMPLATE[template]) {
        totals[key] = totalsCellFor(key)
    }
    return totals
}

/**
 * Rows in display order: pinned domains first, then the rest, each group ordered by the column. A dash sorts
 * last in both directions, so an unavailable figure never reads as the biggest or smallest.
 */
export const sortDomainRows = (rows: DomainRow[], column: string, direction: 'asc' | 'desc'): DomainRow[] => {
    const sign = direction === 'asc' ? 1 : -1
    const byColumn = (first: DomainRow, second: DomainRow): number => {
        const firstValue = first.cells[column]?.value ?? null
        const secondValue = second.cells[column]?.value ?? null
        if (firstValue === null || secondValue === null) {
            return firstValue === secondValue ? 0 : firstValue === null ? 1 : -1
        }
        return (firstValue - secondValue) * sign
    }
    const sorted = [...rows].sort(byColumn)
    return [...sorted.filter((row) => row.pinned), ...sorted.filter((row) => !row.pinned)]
}
